<template>
  <div class="checkout-page">
    <div class="nav-icon">
      <a href="/">
        <img
          src="/assets/images/des_colour.png"
          srcset="/assets/images/des_colour.png?height=80 1x,
           /assets/images/des_colour.png?height=160 2x"
          alt="DES logo"
          class="main-navigation__logo">
      </a>
    </div>
    <error-boundary
      title="Something has gone wrong"
      message="If this keeps happening, please contact us."
      class="checkout-page__wrapper"
      propagate>
      <template #default
                class="checkout-page__content"
                @error="onCheckoutError">
        <checkout
          v-model="currentStepAlias"
          #default
          class="checkout-page__checkout"
          basket-url="/basket"
          expiry-modal-heading="Some Bookings Have Expired"
          @beforeChangeStep="onRequestChangeStep"
          @changeStep="onChangeStep"
          @loading="loading = $event">
          <checkout-progress></checkout-progress>
          <div v-show="loading">
            <div class="loading-container">
              <loading-spinner></loading-spinner>
            </div>
          </div>
          <div v-show="!loading">
            <div class="my-steps">
              <template v-if="guestMode">
                <your-details-step
                  heading="Your details"
                  :stateless="submitted"
                  :web-user-type-parent-id="webUserTypeParentId"
                  :allow-login="!guestMode"
                  :allow-full-registration="!guestMode"
                  :allow-guest-registration="guestMode"
                  :allow-account-details-edit="!guestMode">
                  <template #login>
                    <slot name="login"></slot>
                  </template>

                  <template #why-register>
                    <slot name="why-register"></slot>
                  </template>

                  <template #marketing-label>
                    <slot name="marketing-label"></slot>
                  </template>

                  <template #register-btn>
                    Continue
                  </template>
                </your-details-step>
              </template>
              <template v-else>
                <address-step
                  class="checkout-page__address"
                  :stateless="submitted"
                  :currency="currency"
                  :locale="locale"
                  :web-account-org-id="webAccountOrgId"
                  load-on-mount
                  invoice-heading="Billing addresses"
                  delivery-heading="Delivery addresses"
                  carriage-heading="Postage options"
                  invoice-info="Please confirm your billing address"
                  delivery-info="Please confirm your delivery address"
                  carriage-info="Please select one of the following postage options">
                </address-step>
              </template>

              <basket-step :search-type-id="searchTypeId"
                           ignore-event-delegate-validation
                           :stateless="submitted">
                <template #basket="{events, ...slotProps}">
                  <styled-basket v-bind="slotProps"
                                 enable-vouchers
                                 :delegate-booking-view-model="delegateBookingViewModel"
                                 :relationship-type-ids="relationshipTypeIds"
                                 v-on="events">
                  </styled-basket>
                </template>
              </basket-step>

              <metadata-step :stateless="submitted"
                             metadata-page-message="Please provide the details below."
                             :email-validation-regex="emailRegex"
                             :telephone-validation-regex="telephoneRegex"
                             show-validation>
              </metadata-step>

              <payment-step :terms-link="termsLink"
                            :stateless="submitted"
                            :sealed.sync="submitted"
                            :max-po-ref-length="40"
                            :card-payment-icons="paymentIcons"
                            form-action="GET"
                            hide-on-one-payment-method>
                <template #loading>
                  <div class="loading-container">
                    <loading-spinner></loading-spinner>
                  </div>
                </template>
              </payment-step>

              <confirmation-step
                :assign-subscriptions="assignSubscriptions"
                :assign-events="assignEvents"
                :web-account-org-id="webAccountOrgId"
                :order-header-id="orderHeaderId"
                :subscriptions-count="subscriptionsCount"
                :events-count="eventsCount"
                :user-type="userType"
                hide-event-info
                assign-events-button-text="Assign delegates"
                assign-subscriptions-button-text="Assign licences"
                button-class="btn btn-accent-1"
                @submit="onSubmit">
                <template #loading>
                  <div class="loading-container">
                    <loading-spinner></loading-spinner>
                  </div>
                </template>

                <template #default>
                  <p class="confirmation-step__message">
                    Reminder: Licences can be assigned via the buttons below
                    (where appropriate to your order) or via the admin area, which is accessible
                    to your organisation’s account administrators.
                  </p>
                </template>

                <template #subscription-manager>
                  <subscriptions-manager
                    heading="Assign licences"
                    :subscription-type-id="subscriptionTypeId"
                    enable-groups
                    group-name="user"
                    use-action-icons
                    poll-for-data
                    :user-type="userType"
                    :polling-timeout-ms="30000"
                    :order-header-id="orderHeaderId"
                    :hide-pricing-schedule-text="true"
                    hide-assign-new-group-user
                    hide-event-info
                    booking-table-remove-label="Unassign delegates"
                    booking-table-remove-description="Are you sure you want to
                                          unassign this delegate?"
                    @count="(val) => subscriptionsCount = val">
                    <template #loading>
                      <div class="loading-container">
                        <loading-spinner></loading-spinner>
                      </div>
                    </template>
                  </subscriptions-manager>
                </template>

                <template #events-manager>
                  <!-- eslint-disable max-len -->
                  <events-manager
                    :default-org-id="webAccountOrgId"
                    :hide-pricing-schedule-text="true"
                    :button-class="buttonClass"
                    :order-header-id="orderHeaderId"
                    :user-type="userType"
                    :hide-event-info="true"
                    remove-delegate-label="Unassign delegates"
                    remove-booking-button-tooltip="Unassigning delegates does not cancel the booking"
                    @count="(val) => eventsCount = val">
                  </events-manager>
                  <!-- eslint-enable max-len -->
                </template>
              </confirmation-step>
            </div>
          </div>
        </checkout>
      </template>
    </error-boundary>
  </div>
</template>

<script>
import Vue from "vue";
import VueScrollTo from "vue-scrollto";
import {
  BasketResource,
  AddressStep,
  YourDetailsStep,
  BasketStep,
  PaymentStep,
  ConfirmationStep,
  MetadataStep
} from "spiritweb.packages.ecommerce";
import { SubscriptionsManager, StyleMixin, EventsManager } from "spiritweb.packages.web-account";
import StyledBasket from "~/Components/StyledBasket.vue";
import LoadingSpinner from "~/Components/LoadingSpinner.vue";

// custom implementations of checkout
// import AddressStep from "~/Components/Checkout/AddressStep.vue";
// import PaymentStep from "~/Components/Checkout/PaymentStep.vue";
// import BasketStep from "~/Components/Checkout/BasketStep.vue";
// import BookingStep from "~/Components/Checkout/BookingStep.vue";
// import MetadataStep from "~/Components/Checkout/MetadataStep.vue";
// import ConfirmationStep from "~/Components/Checkout/ConfirmationStep.vue";
// import YourDetailsStep from "~/Components/Checkout/YourDetailsStep.vue";
// import BabcockCheckout from "~/Components/Checkout/Checkout.vue";

Vue.use( VueScrollTo, {
  container: "#app",
  duration: 500,
  easing: "ease",
  offset: 0,
  force: true,
  cancelable: true,
  onStart: false,
  onDone: false,
  onCancel: false,
  x: false,
  y: true
} );

export default {
  name: "CheckoutPage",
  components: {
    YourDetailsStep,
    AddressStep,
    BasketStep,
    StyledBasket,
    PaymentStep,
    ConfirmationStep,
    SubscriptionsManager,
    MetadataStep,
    LoadingSpinner,
    EventsManager
  },
  mixins: [StyleMixin],
  props: {
    termsLink: {
      type: String,
      default: null
    },
    searchTypeId: {
      type: String,
      default: null
    },
    guestMode: {
      type: Boolean,
      default: false
    },
    webUserTypeParentId: {
      type: Number,
      default: null
    },
    currency: {
      type: String,
      default: "GBP"
    },
    locale: {
      type: String,
      default: "en-GB"
    },
    webAccountOrgId: {
      type: String,
      default: null
    },
    assignSubscriptions: {
      type: Boolean,
      default: false
    },
    assignEvents: {
      type: Boolean,
      default: false
    },
    subscriptionTypeId: {
      type: Number,
      default: 3
    },
    orderHeaderId: {
      type: String,
      default: null
    },
    delegateBookingViewModel: {
      type: Object,
      default: () => ( {} )
    },
    webAccountUserTypeId: {
      type: Number,
      default: null
    },
    payNowUrl: {
      type: String,
      default: null
    },
    userType: {
      type: Object,
      default: () => ( {} )
    },
    emailValidationRegex: {
      type: [RegExp, String],
      default: () => /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+$/
    },
    telephoneValidationRegex: {
      type: [RegExp, String],
      default: () => /^\+?[0-9\-\s]{7,15}$/
    }
  },
  data: () => ( {
    currentStepAlias: null,
    loading: true,
    waiting: true,
    hash: "",
    submitted: false,
    subscriptionsCount: 0,
    eventsCount: 0,
    paymentIcons: [
      {
        src: "/Assets/images/default-payment-icons/mastercard.png",
        alt: "Mastercard"
      },
      {
        src: "/Assets/images/default-payment-icons/visa.png",
        alt: "VISA"
      }
    ]
  } ),
  computed: {
    isLoading () {
      return this.loading || this.waiting;
    },
    relationshipTypeIds () {
      return this.userType?.searchRelationshipTypeIds || [];
    },
    emailRegex () {
      return typeof this.emailValidationRegex === "string"
        ? new RegExp( this.emailValidationRegex )
        : this.emailValidationRegex;
    },
    telephoneRegex () {
      return typeof this.telephoneValidationRegex === "string"
        ? new RegExp( this.telephoneValidationRegex )
        : this.telephoneValidationRegex;
    }
  },
  watch: {
    currentStepAlias: {
      handler ( alias ) {
        window.location.hash = alias || "";
      }
    },
    hash ( val ) {
      if ( val !== this.currentStepAlias ) {
        this.currentStepAlias = val;
      }
    }
  },
  beforeCreate () {
    const promise = BasketResource.getItemCount().then( ( { data } ) => {
      if ( !data ) {
        this.submitted = true;
      }
    } );

    return promise;
  },
  created () {
    window.addEventListener( "hashchange", this.onHashChange );
    const hash = this.onHashChange();
    if ( hash ) {
      this.hash = hash;
    }

    this.waiting = false;
  },
  beforeDestroy () {
    window.removeEventListener( "hashchange", this.onHashChange );
  },

  methods: {
    onSubmit ( val ) {
      this.submitted = val;
    },
    onHashChange () {
      const { hash } = window.location;
      let result = null;
      if ( hash ) {
        result = hash.replace( "#", "" );
      }
      this.hash = result;
    },
    onRequestChangeStep ( alias, resolve, reject ) {
      try {
        this.$scrollTo( ".checkout-page", 300, {
          offset: -50,
          onDone: () => resolve()
        } );
      } catch ( e ) {
        reject( e );
      }
    },
    onChangeStep ( step ) {
      this.currentStepAlias = step;
    },
    onCheckoutError () {
      window.location.hash = "error";
    }
  }
};
</script>
<style lang="less" scoped>
@import "../Styles/utils/index.less";

.checkout-page {
  .main-navigation__logo {
    margin-left: 0;
  }

  &__content {
    margin-top: 10px;
    background: @bg-color;
    padding: 30px;
  }
}

/deep/ .checkout-progress {
  margin-bottom: 30px;

  &__list {
    position: relative;
    margin: 0;
    padding: 0 30px;
    background: @bg-color;
    justify-content: space-between;

    &::before {
      content: "";
      position: absolute;
      border-bottom: 2px solid @color-accent-8;
      left: 0;
      right: 0;
      top: 50%;
      z-index: 1;
    }
  }

  &__item {
    display: flex;
    color: @color-accent-4-dark;

    // &--valid {
    //   color: @color-accent-4-dark;
    // }

    &--disabled {
      color: #797979;

      .checkout-progress__btn {
        cursor: not-allowed;
      }
    }

    &--current {
      color: @color-accent-1;
    }
  }

  &__btn {
    position: relative;
    z-index: 2;
    background: @bg-color;
    border: 0;
    color: inherit;
    font-weight: 600;
    font-size: 16px;
    padding: 0 15px;
    margin: 0;
    -webkit-appearance: none;
  }

  @media (max-width: 767px) {
    visibility: hidden;
    display: none;
  }
}

/deep/ .address-step,
/deep/ .payment-step {
  &__footer {
    margin: 0 !important;
  }
}

/deep/ .custom-control-label {
  a {
    .babcock-link();
  }
}

/deep/ .address-selector {
  button {
    .babcock-checkout-action();
  }

  &__search {
    margin: 0 0 10px 0;

    button {
      margin: 0 0 10px 0 !important;
    }
  }

  &__add-new {
    .babcock-checkout-action();
  }
}

/deep/ .address-list__select {
  color: @color-accent-1;

  &--selected {
    color: @color-accent-1-dark;
  }
}

/deep/ .tabbed-step {
  margin-bottom: 0 !important;
  background: #fff;
  padding: 20px 30px 30px;

  &.tabbed-step--invalid {
    .step__continue:not(.no-disabled) {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

/deep/ .step__heading {
  margin-bottom: 30px;
}

/deep/ .step__footer-button {
  .babcock-checkout-action();

  &:last-child {
    margin-right: 0;
  }
}

/deep/ .base-table {
  .base-table__heading {
    font-size: 1.5rem;
    margin-bottom: 15px;
  }

  thead {
    background: #eee;
  }

  .table {
    background: #fff;
    border: 2px solid #bcd8da;
  }

  &__heading-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;

    @media (max-width: 992px) {
      flex-direction: column;
    }
  }

  &__heading-text {
    @media (max-width: 992px) {
      margin-bottom: 1rem;
    }
  }

  &__buttons-container {
    button {
      .babcock-nav-action();
      clear: both;
    }

    display: flex;
    justify-content: flex-end;
    flex-flow: row wrap;
    @button-spacing: 0.625rem;

    margin-left: -@button-spacing;
    margin-top: -@button-spacing;

    > * {
      margin-left: @button-spacing;
      margin-top: @button-spacing;
    }
  }
}

/deep/ .booking-table__assign-users-button,
/deep/ .booking-table__metadata-save-button {
  background-color: @color-accent-1;
  border: 1px solid @color-accent-1;

  &:hover {
    background-color: @color-accent-1-dark;
  }
}

/deep/ .state-manager .state-wrapper .confirmation-step__content {
  max-width: 800px;

  .confirmation-step__message {
    margin-bottom: 30px;
  }

  .confirmation-step__footer {
    margin-top: -10px;

    a,
    button {
      margin-top: 10px
    }
  }
}

/deep/ .events-manager .base-table__sub-heading {
  // display: none;
}

/deep/ .user-events-manager > h1 {
  display: none;
}

/deep/ .assign-panel {
  button {
    .babcock-checkout-action();
  }

  &__back-button {
    padding: 5px 20px !important;
  }

  &__mode-text {
    > a {
      .babcock-link();
    }
  }

  .vs__actions {
    > button {
      background: transparent;
      padding: 0.5rem;
    }

    > svg {
      width: 1.5rem;
    }
  }

  .assign-row {
    display: flex;
    justify-content: flex-end;
    flex-flow: row wrap;
    @content-spacing: 1.875rem;

    margin-left: -@content-spacing;
    margin-top: -@content-spacing;

    > * {
      box-sizing: content-box;
      margin-left: @content-spacing;
      margin-top: @content-spacing;
    }

    &__content {
      flex: 3 3 330px;
    }

    &__actions {
      flex: 1 1 330px;
    }
  }
}

/deep/ .subscriptions-manager {
  + button {
    + a {
      margin-top: 1rem;
    }

    margin-top: 1rem;
  }
}

@media (min-width: 992px) {
  /deep/ .address-selector {
    &__available {
      display: inline-block !important;
    }
  }
}

@media (max-width: 992px) {
  /deep/ .assign-row {
    flex-direction: column;
    margin-bottom: 1rem;

    &__actions {
      flex-basis: 0 !important;
    }

    &__content {
      &:not(:last-child) {
        margin-right: 0 !important;
      }
    }

    &__heading-container {
      flex-direction: column;
    }
  }
}
</style>
